/**
 * Billing-period preview for Login Limit.
 * The start date is a UTC calendar date. Month period n starts on the anchor
 * day of month (start month + n × length), clamped to that month's last day,
 * so Jan 31 gives Feb 28 and then Mar 31; it never steps from a clamped start.
 * A period ends the day before the next starts. Same rule as the gate. The
 * gate's own label is the one stored after save; this preview is what
 * Settings shows while editing.
 */

export type PeriodUnit = 'month' | 'day';

export interface BillingPeriod {
  start: Date;
  /** Inclusive last day of the period. */
  end: Date;
  /** Days from today (UTC) until the next period starts. */
  resetsInDays: number;
  /** The start date is after today, so this is the first period, not the current one. */
  upcoming: boolean;
}

/** Lengths Settings accepts for "Several months" and "A number of days". One month is its own choice. */
export const PERIOD_MONTHS = { min: 2, max: 24 };
export const PERIOD_DAYS = { min: 7, max: 92 };

/**
 * A typed number may stay only when it is already in range, or one more digit
 * can still put it in range. Letters and a value that can never qualify are dropped.
 */
export function periodTextCanBecomeValid(text: string, min: number, max: number): boolean {
  if (!/^\d+$/.test(text) || text.length > 2) return false;
  const value = Number(text);
  if (value > max) return false;
  if (text.length === 2) return value >= min;
  if (value >= min && value <= max) return true;
  for (let digit = 0; digit <= 9; digit += 1) {
    const combined = value * 10 + digit;
    if (combined >= min && combined <= max) return true;
  }
  return false;
}

export function clampPeriodText(raw: string, min: number, max: number): string {
  let out = '';
  for (const ch of raw.replace(/\D/g, '')) {
    const next = out + ch;
    if (!periodTextCanBecomeValid(next, min, max)) break;
    out = next;
  }
  return out;
}

const ENGLISH_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function parseUtcDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return date;
}

function addDays(date: Date, days: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days));
}

function addMonths(anchor: Date, months: number): Date {
  const monthIndex = anchor.getUTCMonth() + months;
  const year = anchor.getUTCFullYear() + Math.floor(monthIndex / 12);
  const month = ((monthIndex % 12) + 12) % 12;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(anchor.getUTCDate(), last)));
}

function periodStart(anchor: Date, index: number, unit: PeriodUnit, length: number): Date {
  return unit === 'day' ? addDays(anchor, index * length) : addMonths(anchor, index * length);
}

export function currentBillingPeriod(
  startIso: string,
  unit: PeriodUnit,
  length: number,
  now: Date = new Date(),
): BillingPeriod | null {
  const anchor = parseUtcDate(startIso);
  if (!anchor || !Number.isInteger(length) || length < 1) return null;
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  let index = 0;
  if (today.getTime() >= anchor.getTime()) {
    let hi = 1;
    while (periodStart(anchor, hi, unit, length).getTime() <= today.getTime()) {
      hi *= 2;
      if (hi > 1_000_000) return null;
    }
    let lo = 0;
    while (lo + 1 < hi) {
      const mid = Math.floor((lo + hi) / 2);
      if (periodStart(anchor, mid, unit, length).getTime() <= today.getTime()) lo = mid;
      else hi = mid;
    }
    index = lo;
  }
  const start = periodStart(anchor, index, unit, length);
  const next = periodStart(anchor, index + 1, unit, length);
  return {
    start,
    end: addDays(next, -1),
    resetsInDays: Math.round((next.getTime() - today.getTime()) / 86_400_000),
    upcoming: today.getTime() < anchor.getTime(),
  };
}

export function formatPeriodDay(date: Date, withYear: boolean, locale: string): string {
  if (locale.toLowerCase().startsWith('en')) {
    const text = `${date.getUTCDate()} ${ENGLISH_MONTHS[date.getUTCMonth()]}`;
    return withYear ? `${text} ${date.getUTCFullYear()}` : text;
  }
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
    ...(withYear ? { year: 'numeric' } : {}),
  }).format(date);
}

export function periodDayLabels(period: BillingPeriod, locale: string): { start: string; end: string } {
  const withYear = period.start.getUTCFullYear() !== period.end.getUTCFullYear();
  return {
    start: formatPeriodDay(period.start, withYear, locale),
    end: formatPeriodDay(period.end, withYear, locale),
  };
}

/** The offset label for a time: `UTC`, `UTC+2`, `UTC-3`, `UTC+5:30`. The same in every language. */
export function utcOffsetLabel(offsetMinutes: number): string {
  if (!Number.isFinite(offsetMinutes) || offsetMinutes === 0) return 'UTC';
  const sign = offsetMinutes > 0 ? '+' : '-';
  const total = Math.abs(Math.round(offsetMinutes));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return `UTC${sign}${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`;
}

/**
 * A gate timestamp in the admin's local time: 24-hour HH:MM and the UTC offset in force
 * at that moment. `withDay` adds the day, in the app language, when it is not today.
 * The time and offset are one left-to-right isolate so Hebrew keeps them together.
 * Empty for an unparseable value.
 */
export function formatLocalTime(iso: string, locale: string, withDay = false, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const today = date.getFullYear() === now.getFullYear()
    && date.getMonth() === now.getMonth()
    && date.getDate() === now.getDate();
  const clock = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(date);
  const time = `${clock} ${utcOffsetLabel(-date.getTimezoneOffset())}`;
  if (!withDay || today) return time;
  const day = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(date);
  return `${day}, \u2066${time}\u2069`;
}
